import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/posthog';

interface CreateOrganizationData {
  name: string;
  timezone: string;
  description?: string;
}

interface CreateOrganizationResult {
  organizationId: string;
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, timezone, description }: CreateOrganizationData): Promise<CreateOrganizationResult> => {
      // Create the organization and enrol the caller as its first admin atomically.
      // The RPC (SECURITY INVOKER) inserts both rows under RLS; the creator-bootstrap
      // policy authorizes the initial membership insert. The timezone is required
      // and validated server-side against pg_timezone_names.
      const { data: organizationId, error } = await supabase.rpc('create_organization_with_owner', {
        p_name: name,
        p_timezone: timezone,
        p_description: description,
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!organizationId) {
        throw new Error('No data returned from organization creation');
      }

      return { organizationId };
    },
    onSuccess: () => {
      trackEvent('organization_created', {});
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
    },
  });
}